import React, {useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import {resetJourney} from '../../app-store/AccountSetupSlice'
import '../../styles/control-center-styles.css'
import ScheduledAppointments from './control-center-options/scheduled-appointments'
import SharedBuyNowLinks from './control-center-options/shared-buy-now-links'
import PendingBuyNowLinks from './control-center-options/pending-buy-now-links'
import CompletedOrders from './control-center-options/completed-orders'
import {getUserRoles} from '../../utils/get-user-roles'
import {resolveKeapPartnerId} from '../../utils/resolveKeapPartnerId'

import SkeletonLoader from '../skeleton-loader'
import {ToolsPage} from '../../screens/tools-page'
import {MyAccounts} from '../../accountManagement/features/AccountsTabs/MyAccounts'
import {AccountSetupPage} from '../../accountManagement/features/AccountSetup'

// Keap partner variant of My Accounts: All Accounts tab hidden + New Account button.
// Renders My Accounts and the New Account setup flow in-pane (no route change) so
// the Control Center side nav stays visible for the entire transaction.
const KeapPartnerAccountManagement = ({keapPartnerInfo}) => {
  const dispatch = useDispatch()
  const [showNewAccount, setShowNewAccount] = React.useState(false)
  return showNewAccount ? (
    <AccountSetupPage
      onExit={() => setShowNewAccount(false)}
      keapPartnerId={keapPartnerInfo?.keapPartnerId}
    />
  ) : (
    <MyAccounts
      isKeapPartner
      keapPartnerId={keapPartnerInfo?.keapPartnerId}
      onNewAccount={() => {
        // Reset the multi-step journey before entering the New Account flow.
        // A prior transaction (e.g. Continue → catalog) advances the slice
        // (currentStep:1, completed:['email-lookup'], stale verifiedEmail) and
        // never resets it, so without this the flow would resume on Account
        // Setup and skip the Email Lookup step entirely.
        dispatch(resetJourney())
        setShowNewAccount(true)
      }}
    />
  )
}

// Default (Keap Partner) options
const DEFAULT_OPTION_COMPONENTS = [
  {label: 'Scheduled Appointments', component: ScheduledAppointments},
  {label: 'Active 1:Many Links', component: SharedBuyNowLinks},
  {label: 'Pending 1:1 Links', component: PendingBuyNowLinks},
  {label: 'Completed Orders', component: CompletedOrders},
  {label: 'Account Management', component: KeapPartnerAccountManagement},
]

const INFLUENCER_OPTION_COMPONENTS = [
  {
    label: 'Control Center',
    isParent: true,
    children: [
      {label: '1:Many Purchase', component: SharedBuyNowLinks},
      {label: 'Pending 1:1 Links', component: PendingBuyNowLinks},
      {label: 'Completed Orders', component: CompletedOrders},
      {label: 'Account Management', component: KeapPartnerAccountManagement},
    ],
  },
  {label: 'Collateral & Education', component: ToolsPage},
]

const renderActiveOptionComponent = (
  option,
  keapPartnerInfo,
  setDashboardLoading,
  dashboardLoading,
  userData,
) => {
  const OptionComponent = option?.component
  return OptionComponent ? (
    <OptionComponent
      keapPartnerInfo={keapPartnerInfo}
      setDashboardLoading={setDashboardLoading}
      dashboardLoading={dashboardLoading}
      title={option.label}
      userData={userData}
    />
  ) : null
}

// `hideAside`: the Keap Partner shell shows these views as side-nav items
// (see side-nav/keap-partner-side-nav.js) and selects one via
// location.state.activePanel.
const ControlCenter = ({userData, keapPartnerId, hideAside = false}) => {
  const keapPartnerIdRef = useRef()
  const location = useLocation()
  const [dashboardLoading, setDashboardLoading] = React.useState(false)

  // Role check - supports both Auth0 and Okta
  const userRoles = getUserRoles(userData)
  const isInfluencerOps = userRoles.includes('InfluencerOps')

  // Choose option set based on role
  const OPTION_COMPONENTS = React.useMemo(
    () =>
      isInfluencerOps
        ? INFLUENCER_OPTION_COMPONENTS
        : DEFAULT_OPTION_COMPONENTS,
    [isInfluencerOps],
  )

  // Active option state (reset when option set changes)
  const getInitialOption = () => {
    const requestedPanel = location.state?.activePanel
    if (requestedPanel) {
      const flat = OPTION_COMPONENTS.flatMap(o => o.children ?? [o])
      const found = flat.find(o => o.label === requestedPanel)
      if (found) return found
    }
    if (isInfluencerOps && OPTION_COMPONENTS[0]?.children) {
      return OPTION_COMPONENTS[0].children[0]
    }
    return OPTION_COMPONENTS[0]
  }

  const [activeOption, setActiveOption] = React.useState(getInitialOption())
  const [expandedParents, setExpandedParents] = React.useState(
    isInfluencerOps ? {'Control Center': true} : {},
  )

  React.useEffect(() => {
    setActiveOption(getInitialOption())
    setExpandedParents(isInfluencerOps ? {'Control Center': true} : {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OPTION_COMPONENTS, location.state?.activePanel])

  keapPartnerIdRef.current = keapPartnerId

  // Use networkId for InfluencerOps users, otherwise use keapPartnerId
  const keapPartnerInfo = {
    keapPartnerId: resolveKeapPartnerId({
      isInfluencerOps,
      storedKeapPartnerId: keapPartnerId,
      user: userData,
    }),
  }

  const toggleParent = parentLabel => {
    setExpandedParents(prev => ({
      ...prev,
      [parentLabel]: !prev[parentLabel],
    }))
  }

  return (
    <section style={{display: 'flex'}}>
      {!hideAside && (
        <aside className="control-center-aside">
          {!isInfluencerOps && (
            <h2 className="control-center-header-text">Control Center</h2>
          )}
          <ul
            style={{
              listStyle: 'none',
              paddingInline: '15px',
              margin: '0',
              minHeight: '100%',
            }}
          >
            {OPTION_COMPONENTS.map((option, index) => (
              <React.Fragment key={index}>
                {option.isParent ? (
                  <>
                    <li
                      className="aside-li-option parent-option"
                      onClick={() => toggleParent(option.label)}
                      style={{
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      {option.label}{' '}
                      <span style={{float: 'right'}}>
                        {expandedParents[option.label] ? '▼' : '▶'}
                      </span>
                    </li>
                    {expandedParents[option.label] &&
                      option.children?.map((child, childIndex) => (
                        <li
                          className={`aside-li-option child-option ${
                            activeOption === child ? 'active' : ''
                          }`}
                          key={`${index}-${childIndex}`}
                          onClick={() => setActiveOption(child)}
                          style={{
                            paddingLeft: '30px',
                          }}
                        >
                          {child.label}
                        </li>
                      ))}
                  </>
                ) : (
                  <li
                    className={`aside-li-option ${
                      activeOption === option ? 'active' : ''
                    }`}
                    onClick={() => setActiveOption(option)}
                  >
                    {option.label}
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        </aside>
      )}

      <section
        style={{
          // background: '#fafafa',
          width: '100%',
          // padding: '16px',
        }}
      >
        {renderActiveOptionComponent(
          activeOption,
          keapPartnerInfo,
          setDashboardLoading,
          dashboardLoading,
          userData,
        )}

        {dashboardLoading && !keapPartnerInfo.keapPartnerId && (
          <SkeletonLoader height="50vh" width="85vw" />
        )}
      </section>
    </section>
  )
}

export default ControlCenter
